import { Injector, InjectionToken, Injectable, ComponentFactoryResolver, Type, ApplicationRef } from '@angular/core';
import { Observable, from } from 'rxjs';
import { IPopup, PopupRef } from './popup-ref';

export type ModalRef = HTMLElement & {
  hide: () => Promise<void>;
  show: () => Promise<void>;
}
export const POPUP = new InjectionToken<string>('popup');
export const DATA = new InjectionToken<string>('data');
declare var XenaDS: {alert: (text: string) => Promise<void>, confirm: (text: string) => Promise<boolean> };

export interface IPopupService {
  open(component: Type<any>, title: string): IPopup;
  alert(text: string, title?: string): Observable<void>;
  confirm(text: string, title?: string): Observable<boolean>;
}

@Injectable({
  providedIn: 'root'
})
export class PopupService implements IPopupService {
  constructor(
    private injector: Injector,
    private resolver: ComponentFactoryResolver,
    private appRef: ApplicationRef
  ) { }

  /**
   * Shows alert.
   * @param text 
   * @param title 
   * @returns Observable that completes when alert is closed.
   */
  alert(text: string, title?: string): Observable<void> {
    const displayString: string = this.buildDisplayString(title, text);
    return from(XenaDS.alert(displayString));
  }

  /**
   * Shows confirm.
   * @param text 
   * @param title 
   * @returns Observable that completes when the confirmbox is closed. Will contain result of the confirm.
   */
  confirm(text: string, title?: string): Observable<boolean> {
    const displayString: string = this.buildDisplayString(title, text);
    return from(XenaDS.confirm(displayString));
  }

  /**
   * Opens a modal containing the specified Component. The component can inject a ModalRef in its constructor to get a reference to the modal.
   * Having the ModalRef in the component allows the component to close the dialog with a result.
   * @param componentType The type of angular component.
   * @returns A reference to the popup.
   */
  open(componentType: Type<any>, title: string, data?: any): IPopup {
    // Create modal element and a PopupRef wrapper for it.
    const modalRef = this.createModal(title);
    const popupRef = new PopupRef(modalRef as ModalRef);

    // Instantiates the Anguar component and appends it to the modal.
    const injector = Injector.create({providers: [{provide: POPUP, useValue: popupRef}, {provide: DATA, useValue: data}], parent: this.injector});
    const componentRef = this.resolver.resolveComponentFactory(componentType).create(injector);
    componentRef.changeDetectorRef.detectChanges();

    this.appRef.attachView(componentRef.hostView);
    modalRef.appendChild(componentRef.location.nativeElement);

    // Display the modal and return ref to caller
    this.displayWhenHydrated(modalRef);

    popupRef.afterClosed().subscribe(() => {
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
    });
    
    return popupRef;
  }

  private displayWhenHydrated(modalRef: ModalRef) {
    const ttl = 200;
    const start = new Date();
    const interval = setInterval(() => {
      const now = new Date();
      const ttlExpired = now.getTime() - start.getTime() > ttl;
      const isHydrated = modalRef.classList.contains('hydrated');
      if(ttlExpired || isHydrated) {
        clearInterval(interval);
        modalRef.setAttribute('open', 'true')
      }
    }, 10);
  }

  private createModal(title: string): ModalRef {
    const modal = document.createElement('xena-modal');
    modal.setAttribute('label', title);
    document.body.appendChild(modal);
    return modal as ModalRef;
  }

  private buildDisplayString(title: string, text: string) {
    let displayString: string;
    if (title != null) {
      displayString = `${title} - ${text}`;
    } else {
      displayString = text;
    }
    return displayString;
  }
}
